/**
 * Service for managing user preferences via the backend API.
 */
import { authFetch } from '../host/utils/auth';
import { getApiBaseUrl } from '../host/utils/http';

const API_BASE = () => `${getApiBaseUrl()}/secure-auth`;

class PreferenceService {
  constructor() {
    this.cache = null;
    this.cacheExpiry = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get user preferences from the backend.
   * @returns {Promise<Object>} User preferences
   */
  async getPreferences() {
    // Check cache
    if (this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    try {
      const response = await authFetch(`${API_BASE()}/preferences`);

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();

      // Update cache
      this.cache = data.preferences;
      this.cacheExpiry = Date.now() + this.cacheTimeout;

      return data.preferences;
    } catch (error) {
      console.error('Error fetching preferences:', error);

      // Return cached data if available
      if (this.cache) {
        return this.cache;
      }

      // Return defaults if no cache
      return this.getDefaultPreferences();
    }
  }

  /**
   * Update user preferences.
   * @param {Object} preferences - Preferences to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updatePreferences(preferences) {
    try {
      const response = await authFetch(`${API_BASE()}/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }

      const data = await response.json();

      // Update cache
      this.cache = data.preferences;
      this.cacheExpiry = Date.now() + this.cacheTimeout;

      return data.preferences;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Reset preferences to defaults.
   * @returns {Promise<Object>} Default preferences
   */
  async resetPreferences() {
    try {
      const response = await authFetch(`${API_BASE()}/preferences`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to reset preferences');
      }

      const data = await response.json();

      // Clear cache
      this.cache = data.preferences;
      this.cacheExpiry = Date.now() + this.cacheTimeout;

      return data.preferences;
    } catch (error) {
      console.error('Error resetting preferences:', error);
      throw error;
    }
  }

  /**
   * Update a single preference.
   * @param {string} key - Preference key
   * @param {any} value - Preference value
   * @returns {Promise<Object>} Updated preferences
   */
  async updatePreference(key, value) {
    const updates = { [key]: value };
    return this.updatePreferences(updates);
  }

  /**
   * Get theme preference.
   * @returns {Promise<string>} Theme preference ('light', 'dark', or 'auto')
   */
  async getTheme() {
    const preferences = await this.getPreferences();
    return preferences.theme || 'light';
  }

  /**
   * Set theme preference.
   * @param {string} theme - Theme to set ('light', 'dark', or 'auto')
   * @returns {Promise<Object>} Updated preferences
   */
  async setTheme(theme) {
    return this.updatePreference('theme', theme);
  }

  /**
   * Get notification settings.
   * @returns {Promise<Object>} Notification settings
   */
  async getNotificationSettings() {
    const preferences = await this.getPreferences();
    return preferences.notifications || this.getDefaultPreferences().notifications;
  }

  /**
   * Update notification settings.
   * @param {Object} settings - Notification settings to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateNotificationSettings(settings) {
    return this.updatePreference('notifications', settings);
  }

  /**
   * Clear the preference cache.
   */
  clearCache() {
    this.cache = null;
    this.cacheExpiry = null;
  }

  /**
   * Get default preferences.
   * @returns {Object} Default preferences
   */
  getDefaultPreferences() {
    return {
      theme: 'light',
      sidebar_collapsed: false,
      language: 'en',
      timezone: 'UTC',
      date_format: 'MM/DD/YYYY',
      time_format: '12h',
      notifications: {
        email: true,
        in_app: true,
        sound: false,
        desktop: false
      },
      accessibility: {
        high_contrast: false,
        reduce_motion: false,
        screen_reader: false,
        font_size: 'medium'
      },
      workspace: {
        default_app: 'home',
        auto_save: true,
        auto_save_interval: 30,
        confirm_navigation: true,
        show_tooltips: true
      }
    };
  }

  /**
   * Migrate legacy preferences from localStorage.
   * @param {string} userId - User ID
   */
  async migrateLegacyPreferences(userId) {
    const legacyKeys = [
      `theme_${userId}`,
      `sidebar_${userId}`,
      `language_${userId}`
    ];

    const legacyData = {};
    let hasLegacyData = false;

    legacyKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        hasLegacyData = true;
        const keyName = key.split('_')[0];
        legacyData[keyName] = value;
      }
    });

    if (hasLegacyData) {
      try {
        // Update preferences with legacy data
        await this.updatePreferences(legacyData);

        // Clean up localStorage
        legacyKeys.forEach(key => localStorage.removeItem(key));

        console.log('Legacy preferences migrated successfully');
      } catch (error) {
        console.error('Failed to migrate legacy preferences:', error);
      }
    }
  }
}

// Create singleton instance
export const preferenceService = new PreferenceService();

// Export hooks for React components
export const usePreferences = () => {
  const [preferences, setPreferences] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        const prefs = await preferenceService.getPreferences();
        setPreferences(prefs);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const updatePreferences = async (updates) => {
    try {
      const updated = await preferenceService.updatePreferences(updates);
      setPreferences(updated);
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resetPreferences = async () => {
    try {
      const defaults = await preferenceService.resetPreferences();
      setPreferences(defaults);
      return defaults;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    resetPreferences
  };
};
